import {Box, Flex, FormLabel, Text} from '@chakra-ui/react';
import { currencyFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import SelectInput from "../../../../../components/inputs/SelectInput";
import {generateSelectOptions} from "../../../../../helpers/inputHelper";

const CHART_TYPE = {
  BAR: 'bar',
  AREA: 'area',
};

const CHART_TYPE_META = {
  [CHART_TYPE.BAR]: {
    label: 'Bar',
  },
  [CHART_TYPE.AREA]: {
    label: 'Area',
  },
};

const SpendBySourceBarChart = ({ data, date }) => {
  const [chartData, setChartData] = useState();
  const [chartType, setChartType] = useState(CHART_TYPE.AREA);

  useEffect(() => {
    if (data) {
      const _chartData = getChartData();
      setChartData(_chartData);
    }
  }, [data, chartType]);

  const getCategoryLabel = (mDate, index) => {
    if (index === 0 || mDate.date() === 1) {
      return [mDate.format('D'), mDate.format('MMMM')];
    }

    return mDate.format('D');
  };

  const getChartOptions = ({ dates, xAxisCategories, maxTotal }) => {
    return {
      chart: {
        fontFamily: 'DM Sans',
        stacked: true,
        animations:{
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({series, seriesIndex, dataPointIndex, w}) {

          const title = w.config.dates[dataPointIndex].format('D MMM YYYY');

          let tooltipItems = []
          const total = w.config.series.reduce((t, serie) => t + serie.data[dataPointIndex], 0);
          w.config.series.forEach((serie) => {
            tooltipItems.push({
              label: serie.name,
              value: currencyFormat(serie.data[dataPointIndex]),
              color: serie.color,
            });
          })

          tooltipItems.push(
              { html: "---" },
              { label: 'Total', value: currencyFormat(total) }
          )

          return renderToString(<ChartTooltip title={title} items={tooltipItems}/>);
        },
      },
      xaxis: {
        categories: xAxisCategories,
      },
      yaxis: {
        min: 0,
        max: () => {
          return maxTotal;
        },
        labels: {
          formatter: (value) => {
            if (value === null) {
              return null;
            }
            return currencyFormat(value);
          },
        },
      },
      dates,
    };
  };

  const getChartData = () => {
    const _data = data.map((row) => {
      let rowData = {
        date: moment({
          year: row.date.year,
          month: row.date.month - 1,
          day: row.date.day,
        }),
        Source: row.Source,
        spend: row.spend || 0,
      };

      return rowData;
    });

    let xAxisCategories = [];
    let dates = [];
    // let datesDict = {};
    let maxTotal = 0;

    let spendBySource = {}
    let spendBySourceDict = {}
    let spendByDateDict = {};
    let indexByDateDict = {};

    const series = []

    for (let i = 60; i >= 0; i--) {
      const day = moment(date).subtract(i, 'days')
      // datesDict[day.toString()] = true;
      xAxisCategories.push(getCategoryLabel(day, 60-i));
      dates.push(day);
      indexByDateDict[day.toString()] = 60-i;
      spendByDateDict[day.toString()] = 0
    }

    _data.forEach((row, index) => {
      if (spendByDateDict.hasOwnProperty(row.date.toString())) {
        spendByDateDict[row.date.toString()] += row.spend || 0
      }

      if (!(row.Source in spendBySourceDict)) {
        spendBySourceDict[row.Source] = true;
        spendBySource[row.Source] = Array(dates.length).fill(0);
      }
    });

    _data.forEach((row, index) => {
      const dateIndex = indexByDateDict[row.date.toString()];
      spendBySource[row.Source][dateIndex] += row.spend;
    });

    for (const key in spendByDateDict) {
      if (spendByDateDict.hasOwnProperty(key)) {
        if (spendByDateDict[key] > maxTotal) {
          maxTotal = spendByDateDict[key];
        }
      }
    }

    const colorPalette = [
      "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
      "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
      "#393b79", "#637939", "#8c6d31", "#843c39", "#7b4173",
      "#3182bd", "#e6550d", "#31a354", "#756bb1", "#636363",
      "#fdae6b", "#9ecae1", "#74c476", "#fd8d3c", "#c994c7",
      "#6baed6", "#9e9ac8", "#ff9896", "#98df8a", "#ffbb78",
      "#aec7e8", "#c5b0d5", "#c49c94", "#f7b6d2", "#dbdb8d",
      "#17becf", "#8dd3c7", "#ffffb3", "#bebada", "#fb8072",
      "#80b1d3", "#fdb462", "#b3de69", "#fccde5", "#d9d9d9",
      "#bc80bd", "#ccebc5", "#ffed6f", "#1b9e77", "#d95f02"
    ];

    let colorIndex = 0;
    for (const key in spendBySourceDict) {
      if (spendBySourceDict.hasOwnProperty(key)) {
        series.push({
          name: key,
          data: spendBySource[key],
          color: colorPalette[colorIndex % colorPalette.length],
        });
        colorIndex++;
      }
    }

    const options1 = getChartOptions({
      dates,
      xAxisCategories,
      maxTotal: maxTotal,
    });

    return {
      series: series,
      options: options1,
      chartType: chartType,
    };
  };

  if (!chartData) {
    return null;
  }

  // const chartTypeOption = [
  //   { value: "bar", label: 'Bar' },
  //   { value: "area", label: 'Area' },
  //   { value: "line", label: 'Line' },
  // ];

  return (
    <>
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Text fontWeight={800} mb={10}>
          Marketing Daily Spend By Source
        </Text>
        <Flex align={'center'} gap={1}>
          <FormLabel m={0}>Chart type: </FormLabel>
          <SelectInput
              options={generateSelectOptions({ metaObj: CHART_TYPE_META })}
              value={chartType}
              onChange={setChartType}
              w={200}
          />
        </Flex>
      </Flex>
      <Box minH={300}>
        <Chart
          type={chartData.chartType}
          series={chartData.series}
          options={chartData.options}
          width={'100%'}
          height={'100%'}
        />
      </Box>
    </>
  );
};

SpendBySourceBarChart.propTypes = {
  data: PropTypes.array,
};

export default SpendBySourceBarChart;
